<script setup lang="ts">
const localePath = useLocalePath()
const { t } = useI18n();
useHead({
  titleTemplate: () => t("seo.layouts./viber-business-messages.title"),
  meta: [
    { name: "description", content: () => t("seo.layouts./viber-business-messages.description") },
    {
      property: "og:title", content: () => t("seo.layouts./viber-business-messages.title")
    },
    { property: "og:description", content: () => t("seo.layouts./viber-business-messages.description") },
    {
      property: "og:image",
      content:
        "https://cdn-web.smsbat.com/uploads/2024/viber_bm.png",
    },
    { property: "og:image:width", content: "972" },
    { property: "og:image:height", content: "972" },
    { property: "og:image:type", content: "image/png" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: "@smsbatcom" },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "SMSBAT Viber Messaging API",
        "applicationCategory": "BusinessApplication",
        "description": "API platform for sending Viber Business Messages including OTP, transactional notifications and promotional campaigns"
      })
    }
  ]
});

const popularQuestions = ref([
  {
    question: t('viberBusinessMessagesSEO.faqBlock.q1'),
    answer: t('viberBusinessMessagesSEO.faqBlock.a1'),
    isOpen: false
  },
  {
    question: t('viberBusinessMessagesSEO.faqBlock.q2'),
    answer: t('viberBusinessMessagesSEO.faqBlock.a2'),
    isOpen: false
  },
  {
    question: t('viberBusinessMessagesSEO.faqBlock.q3'),
    answer: t('viberBusinessMessagesSEO.faqBlock.a3'),
    isOpen: false
  },
  {
    question: t('viberBusinessMessagesSEO.faqBlock.q4'),
    answer: t('viberBusinessMessagesSEO.faqBlock.a4'),
    isOpen: false
  }
])

onMounted(() => {
  const el = document.querySelector("body")
  el?.scrollIntoView()
  if (globalThis.location.hash) {
    const el = document.querySelector(globalThis.location.hash)
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth' })
      }, 500)
    }
  }
})

</script>

<template>
  <div>
    <div class="main-banner">
      <div class="main-banner-content">
        <div class="banner-text">
          <div class="banner-title">
            {{ $t('bm.title') }}
          </div>
          <div class="banner-description">
            {{ $t('bm.subtitle') }}
          </div>
          <div class="banner-action">
            <NuxtLink to="/pricing/viber-business-messages/" class="nav-link button filled whitespace-nowrap !text-sm">
              <p>{{ $t('actions.tryForFree') }}</p>
            </NuxtLink>
            <NuxtLink to="viber://chat?service=26076" class="button outlined" external>{{ $t('actions.contactUs') }}
            </NuxtLink>
          </div>
        </div>
        <div class="banner-image-container">
          <NuxtImg src="https://cdn-web.smsbat.com/uploads/2024/viber-for-business.svg" width="800" height="600" format="webp" loading="eager" fetchpriority="high" class="w-full h-auto object-contain" />
        </div>
      </div>
    </div>

    <!-- What are Viber Business Messages? -->
    <section class="page-section bg-white pt-12 pb-8">
      <div class="container mx-auto max-w-5xl px-4">
        <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{{ $t('viberBusinessMessagesSEO.definition.title') }}</h2>
        <p class="text-xl text-gray-700 mb-4">{{ $t('viberBusinessMessagesSEO.definition.intro') }}</p>
        <ul class="list-disc list-inside text-lg text-gray-700 mb-6 ml-4 space-y-2">
          <li v-for="item in $t('viberBusinessMessagesSEO.definition.list').split('\n')" :key="item">{{ item }}</li>
        </ul>
        <p class="text-lg text-gray-700 mb-8">{{ $t('viberBusinessMessagesSEO.definition.outro') }}</p>
        
        <!-- Internal Links (SEO) -->
        <div class="flex flex-wrap gap-4 mt-8">
           <NuxtLink :to="localePath('/viber-transactional-messages')" class="bg-blue-50 text-blue-800 px-6 py-3 rounded-lg font-medium hover:bg-blue-100 transition">Viber Transactional</NuxtLink>
           <NuxtLink :to="localePath('/viber-otp')" class="bg-blue-50 text-blue-800 px-6 py-3 rounded-lg font-medium hover:bg-blue-100 transition">Viber OTP</NuxtLink>
           <NuxtLink :to="localePath('/viber-chatbots')" class="bg-blue-50 text-blue-800 px-6 py-3 rounded-lg font-medium hover:bg-blue-100 transition">Viber Chatbots</NuxtLink>
           <NuxtLink :to="localePath('/pricing/viber/ua')" class="bg-blue-50 text-blue-800 px-6 py-3 rounded-lg font-medium hover:bg-blue-100 transition">Viber Pricing</NuxtLink>
        </div>
      </div>
    </section>

    <!-- Viber Messaging Use Cases -->
    <section class="page-section bg-gray-50 py-12">
      <div class="container mx-auto max-w-6xl px-4">
        <h2 class="text-3xl font-bold text-center mb-10">{{ $t('viberBusinessMessagesSEO.useCases.title') }}</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 class="text-xl font-bold text-blue-800 mb-4 flex items-center"><i class="pi pi-shopping-cart mr-2"></i>{{ $t('viberBusinessMessagesSEO.useCases.ecommerce') }}</h3>
            <ul class="space-y-2 text-gray-600">
              <li v-for="item in $t('viberBusinessMessagesSEO.useCases.ecommerceList').split('\n')" :key="item" class="flex"><i class="pi pi-check text-green-500 mr-2 mt-1"></i>{{ item }}</li>
            </ul>
          </div>
          <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 class="text-xl font-bold text-blue-800 mb-4 flex items-center"><i class="pi pi-building-columns mr-2"></i>{{ $t('viberBusinessMessagesSEO.useCases.banking') }}</h3>
            <ul class="space-y-2 text-gray-600">
              <li v-for="item in $t('viberBusinessMessagesSEO.useCases.bankingList').split('\n')" :key="item" class="flex"><i class="pi pi-check text-green-500 mr-2 mt-1"></i>{{ item }}</li>
            </ul>
          </div>
          <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 class="text-xl font-bold text-blue-800 mb-4 flex items-center"><i class="pi pi-truck mr-2"></i>{{ $t('viberBusinessMessagesSEO.useCases.logistics') }}</h3>
            <ul class="space-y-2 text-gray-600">
              <li v-for="item in $t('viberBusinessMessagesSEO.useCases.logisticsList').split('\n')" :key="item" class="flex"><i class="pi pi-check text-green-500 mr-2 mt-1"></i>{{ item }}</li>
            </ul>
          </div>
          <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 class="text-xl font-bold text-blue-800 mb-4 flex items-center"><i class="pi pi-headphones mr-2"></i>{{ $t('viberBusinessMessagesSEO.useCases.support') }}</h3>
            <ul class="space-y-2 text-gray-600">
              <li v-for="item in $t('viberBusinessMessagesSEO.useCases.supportList').split('\n')" :key="item" class="flex"><i class="pi pi-check text-green-500 mr-2 mt-1"></i>{{ item }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Viber vs SMS for Business Messaging -->
    <section class="page-section bg-white py-12">
      <div class="container mx-auto max-w-4xl px-4">
        <h2 class="text-3xl font-bold text-center mb-10">{{ $t('viberBusinessMessagesSEO.comparison.title') }}</h2>
        <div class="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-50 text-gray-800">
                <th class="p-4 border-b font-bold">{{ $t('viberBusinessMessagesSEO.comparison.feature') }}</th>
                <th class="p-4 border-b font-bold text-center">{{ $t('viberBusinessMessagesSEO.comparison.sms') }}</th>
                <th class="p-4 border-b font-bold text-center text-[#7360f2]">{{ $t('viberBusinessMessagesSEO.comparison.viber') }}</th>
              </tr>
            </thead>
            <tbody class="text-gray-600">
              <tr>
                <td class="p-4 border-b">{{ $t('viberBusinessMessagesSEO.comparison.branding') }}</td>
                <td class="p-4 border-b text-center">{{ $t('viberBusinessMessagesSEO.comparison.no') }}</td>
                <td class="p-4 border-b text-center font-bold text-[#7360f2]">{{ $t('viberBusinessMessagesSEO.comparison.yes') }}</td>
              </tr>
              <tr class="bg-gray-50">
                <td class="p-4 border-b">{{ $t('viberBusinessMessagesSEO.comparison.buttons') }}</td>
                <td class="p-4 border-b text-center">{{ $t('viberBusinessMessagesSEO.comparison.no') }}</td>
                <td class="p-4 border-b text-center font-bold text-[#7360f2]">{{ $t('viberBusinessMessagesSEO.comparison.yes') }}</td>
              </tr>
              <tr>
                <td class="p-4 border-b">{{ $t('viberBusinessMessagesSEO.comparison.images') }}</td>
                <td class="p-4 border-b text-center">{{ $t('viberBusinessMessagesSEO.comparison.no') }}</td>
                <td class="p-4 border-b text-center font-bold text-[#7360f2]">{{ $t('viberBusinessMessagesSEO.comparison.yes') }}</td>
              </tr>
              <tr class="bg-gray-50">
                <td class="p-4 border-b">{{ $t('viberBusinessMessagesSEO.comparison.cost') }}</td>
                <td class="p-4 border-b text-center">{{ $t('viberBusinessMessagesSEO.comparison.higher') }}</td>
                <td class="p-4 border-b text-center font-bold text-[#7360f2]">{{ $t('viberBusinessMessagesSEO.comparison.lower') }}</td>
              </tr>
              <tr>
                <td class="p-4 border-b">{{ $t('viberBusinessMessagesSEO.comparison.engagement') }}</td>
                <td class="p-4 border-b text-center">{{ $t('viberBusinessMessagesSEO.comparison.medium') }}</td>
                <td class="p-4 border-b text-center font-bold text-[#7360f2]">{{ $t('viberBusinessMessagesSEO.comparison.high') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- Viber Business Messages API -->
    <section class="page-section bg-[#0B1120] text-gray-300 py-12">
      <div class="container mx-auto max-w-5xl px-4">
        <h2 class="text-3xl font-bold text-white mb-6">{{ $t('viberBusinessMessagesSEO.api.title') }}</h2>
        <p class="text-lg mb-8 max-w-3xl">{{ $t('viberBusinessMessagesSEO.api.intro') }}</p>
        
        <div class="bg-[#1F2937] rounded-xl overflow-hidden shadow-2xl border border-gray-700">
          <div class="flex items-center px-4 py-2 border-b border-gray-700 bg-[#374151]">
            <div class="flex space-x-2">
              <div class="w-3 h-3 rounded-full bg-red-500"></div>
              <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div class="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            <div class="mx-auto text-xs text-gray-400 font-mono">POST /v1/messages</div>
          </div>
          <div class="p-6 overflow-x-auto text-sm font-mono leading-relaxed text-green-400">
            <pre><code>{
  "messages": [
    {
      "from": "YourBrand",
      "to": "380931234567",
      "type": "viber_promo",
      "text": "Hello from SMSBAT"
    }
  ]
}</code></pre>
          </div>
        </div>
      </div>
    </section>

    <!-- How it Works -->
    <section class="page-section bg-white py-12">
      <div class="container mx-auto max-w-5xl px-4">
        <h2 class="text-3xl font-bold text-center mb-10">{{ $t('viberBusinessMessagesSEO.howItWorks.title') }}</h2>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
          <div class="flex flex-col items-center text-center relative z-10">
            <div class="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-2xl font-bold mb-4 shadow-sm">1</div>
            <p class="font-medium text-gray-800">{{ $t('viberBusinessMessagesSEO.howItWorks.step1') }}</p>
          </div>
          <div class="flex flex-col items-center text-center relative z-10">
            <div class="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-2xl font-bold mb-4 shadow-sm">2</div>
            <p class="font-medium text-gray-800">{{ $t('viberBusinessMessagesSEO.howItWorks.step2') }}</p>
          </div>
          <div class="flex flex-col items-center text-center relative z-10">
            <div class="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-2xl font-bold mb-4 shadow-sm">3</div>
            <p class="font-medium text-gray-800">{{ $t('viberBusinessMessagesSEO.howItWorks.step3') }}</p>
          </div>
          <div class="flex flex-col items-center text-center relative z-10">
            <div class="w-16 h-16 bg-[#3070b6] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-4 shadow-md">4</div>
            <p class="font-medium text-gray-800">{{ $t('viberBusinessMessagesSEO.howItWorks.step4') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Businesses Choose SMSBAT -->
    <section class="page-section bg-blue-50 py-12">
      <div class="container mx-auto max-w-4xl px-4 text-center">
        <h2 class="text-3xl font-bold text-gray-900 mb-8">{{ $t('viberBusinessMessagesSEO.whyChoose.title') }}</h2>
        <div class="flex flex-wrap justify-center gap-4">
          <div v-for="reason in $t('viberBusinessMessagesSEO.whyChoose.list').split('\n')" :key="reason" class="bg-white px-6 py-3 rounded-full shadow-sm text-gray-700 font-medium flex items-center border border-gray-100">
            <i class="pi pi-check-circle text-blue-500 mr-2"></i> {{ reason }}
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Explanation (Before Rates Table) -->
    <div class="page-section bg-white pt-12 pb-4" id="rates" ref="rates">
      <div class="container mx-auto max-w-5xl px-4">
        <div class="service-price mb-8">
          <div class="price-label">{{ $t('global.price') }}</div>
        </div>
        <div class="text-center mb-8">
          <h2 class="text-3xl md:text-4xl font-bold">{{ $t('viberBusinessMessagesSEO.pricing.title') }}</h2>
        </div>
        
        <div class="bg-gray-50 p-6 md:p-8 rounded-2xl mb-10 border border-gray-100">
          <p class="text-lg text-gray-700 mb-6 whitespace-pre-line">{{ $t('viberBusinessMessagesSEO.pricing.intro') }}</p>
          
          <div class="overflow-x-auto rounded-lg border border-gray-200">
            <table class="w-full text-left bg-white">
              <thead>
                <tr class="bg-gray-100 text-gray-700">
                  <th class="p-3 border-b font-bold">{{ $t('viberBusinessMessagesSEO.pricing.type') }}</th>
                  <th class="p-3 border-b font-bold">{{ $t('viberBusinessMessagesSEO.pricing.desc') }}</th>
                </tr>
              </thead>
              <tbody class="text-gray-600">
                <tr>
                  <td class="p-3 border-b font-medium text-blue-800">{{ $t('viberBusinessMessagesSEO.pricing.otpType') }}</td>
                  <td class="p-3 border-b">{{ $t('viberBusinessMessagesSEO.pricing.otpDesc') }}</td>
                </tr>
                <tr class="bg-gray-50">
                  <td class="p-3 border-b font-medium text-blue-800">{{ $t('viberBusinessMessagesSEO.pricing.txnType') }}</td>
                  <td class="p-3 border-b">{{ $t('viberBusinessMessagesSEO.pricing.txnDesc') }}</td>
                </tr>
                <tr>
                  <td class="p-3 border-b font-medium text-blue-800">{{ $t('viberBusinessMessagesSEO.pricing.promoType') }}</td>
                  <td class="p-3 border-b">{{ $t('viberBusinessMessagesSEO.pricing.promoDesc') }}</td>
                </tr>
                <tr class="bg-gray-50">
                  <td class="p-3 border-b font-medium text-blue-800">{{ $t('viberBusinessMessagesSEO.pricing.sessionType') }}</td>
                  <td class="p-3 border-b">{{ $t('viberBusinessMessagesSEO.pricing.sessionDesc') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="mb-10">
      <BMDefaultRatesTable />
    </div>

    <div class="page-section">
      <div class="section-content">
        <div class="text-center">
          <a href="https://omni.smsbat.com/register-ca" target="_blank" class="nav-link">
            <p class="nav-link button filled w-1/3 mx-auto">{{ $t('links.registerBusinessAccount') }}</p>
          </a>
        </div>
      </div>
    </div>

    <div class="page-section">
      <div class="container">
        <ClientOnly>
          <PopularQuestions :title="$t('chats.questionsTitle')" :questions="popularQuestions" />
        </ClientOnly>
      </div>
    </div>

  </div>
</template>

<style lang="scss" scoped>
.about-item img {
  width: 100%;
}

.banner-image-container {
  width: 100%;
  margin-top: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 900px) {
  .main-banner-content {
    display: block;
  }
}

.main-banner .banner-text {
  margin-left: 0;
  align-self: center;
  max-width: 700px;
}

.main-banner-content {
  padding: 0;
}

@media (min-width: 768px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1170px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1300px) {
  .main-banner .banner-text {
    margin-left: calc((100% - 1300px) / 2);
    max-width: 700px;
  }
}
</style>
